"use client";

import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import nowadaysIcon from "@/app/(public)/shared/assets/nowadays-icon.png";
import { Button } from "@/app/(public)/shared/ui/Button";
import ThirdPartyToggle from "@/components/ui/comparisons/ThirdPartyToggle";
import { fetchCompetitors } from "@/lib/publications/api";
import type { CompetitorPost } from "@/lib/publications/types";
import { Clock } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import Link from "next/link";

// Professional VS Card Component
interface VSCardProps {
  competitor: CompetitorPost;
  onClick: () => void;
}

const VSCard = ({ competitor, onClick }: VSCardProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div
      className="group border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-gray-300 transition-colors bg-white"
      onClick={onClick}
    >
      {/* VS Layout */}
      <div className="flex items-center justify-between mb-4">
        {/* Competitor Logo */}
        <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center overflow-hidden">
          <img
            src={competitor.competitorLogo.url}
            alt={competitor.competitorLogo.alt}
            className="w-8 h-8 object-contain"
          />
        </div>

        {/* VS Text */}
        <div className="text-xs font-medium text-gray-400">vs</div>

        {/* Nowadays Logo */}
        <div className="w-12 h-12 bg-gray-50 rounded flex items-center justify-center overflow-hidden">
          <img src={nowadaysIcon.src} alt="Nowadays Icon" className="w-9 h-9 object-contain" />
        </div>
      </div>

      {/* Title */}
      <h3 className="text-sm font-medium text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
        {competitor.title}
      </h3>

      {/* Meta Info */}
      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-1">
          <Clock size={12} />
          <span>{competitor.readTime} min read</span>
        </div>
        <div>•</div>
        <div>Updated {formatDate(competitor.updatedAt)}</div>
      </div>

      {/* Tags */}
      {competitor.tags && competitor.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {competitor.tags.slice(0, 2).map((tag, index) => (
            <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
              {tag}
            </span>
          ))}
          {competitor.tags.length > 2 && (
            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">+{competitor.tags.length - 2}</span>
          )}
        </div>
      )}
    </div>
  );
};

// Skeleton Card Component
const VSCardSkeleton = () => {
  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      {/* VS Layout Skeleton */}
      <div className="flex items-center justify-between mb-4">
        {/* Nowadays Logo */}
        <div className="w-12 h-12 bg-gray-200 rounded animate-pulse" />

        {/* VS Text */}
        <div className="w-4 h-3 bg-gray-200 rounded animate-pulse" />

        {/* Competitor Logo */}
        <div className="w-12 h-12 bg-gray-200 rounded animate-pulse" />
      </div>

      {/* Title Skeleton */}
      <div className="w-4/5 h-4 bg-gray-200 rounded animate-pulse mb-2" />

      {/* Meta Info Skeleton */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-16 h-3 bg-gray-200 rounded animate-pulse" />
        <div className="w-1 h-1 bg-gray-200 rounded-full animate-pulse" />
        <div className="w-20 h-3 bg-gray-200 rounded animate-pulse" />
      </div>

      {/* Tags Skeleton */}
      <div className="flex flex-wrap gap-1">
        <div className="w-12 h-5 bg-gray-200 rounded animate-pulse" />
        <div className="w-16 h-5 bg-gray-200 rounded animate-pulse" />
        <div className="w-8 h-5 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  );
};

// Main Competitor Page Component (Inner)
function CompetitorInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { openModal, closeModal, isModalOpen } = useModalManager();
  const [competitors, setCompetitors] = useState<CompetitorPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isThirdParty, setIsThirdParty] = useState(false);

  // Check for 3rdparty parameter
  useEffect(() => {
    const thirdParty = searchParams?.get("3rdparty") === "true";
    setIsThirdParty(thirdParty);

    // Store in sessionStorage to persist across navigation
    if (thirdParty) {
      sessionStorage.setItem("3rdparty", "true");
    }
  }, [searchParams]);

  // Fetch competitors on component mount
  useEffect(() => {
    const loadCompetitors = async () => {
      try {
        setLoading(true);
        setError(null);
        const competitorPosts = await fetchCompetitors();
        setCompetitors(competitorPosts);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load competitors");
        console.error("Error fetching competitors:", err);
      } finally {
        setLoading(false);
      }
    };

    loadCompetitors();
  }, []);

  const handleCompetitorClick = (competitor: CompetitorPost) => {
    // Preserve 3rdparty parameter when navigating
    const url = isThirdParty ? `/comparisons/${competitor.slug}?3rdparty=true` : `/comparisons/${competitor.slug}`;
    router.push(url);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white" data-section="competitor">
        {/* Hero Section Skeleton */}
        <div className="">
          <div className="container-page container-top">
            <div className="max-w-3xl mx-auto text-center flex flex-col gap-24 pt-24">
              <div className="w-80 h-9 bg-gray-200 rounded animate-pulse mx-auto" />
              <div className="w-96 h-7 bg-gray-200 rounded animate-pulse mx-auto" />
            </div>
          </div>
        </div>

        {/* Comparisons Grid Skeleton */}
        <div className="container-page py-16">
          {/* Featured Comparisons Skeleton */}
          <div className="mb-16">
            <div className="w-48 h-6 bg-gray-200 rounded animate-pulse mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, index) => (
                <VSCardSkeleton key={index} />
              ))}
            </div>
          </div>

          {/* All Comparisons Skeleton */}
          <div>
            <div className="w-40 h-6 bg-gray-200 rounded animate-pulse mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <VSCardSkeleton key={index} />
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section Skeleton */}
        <div className="border-t border-gray-200">
          <div className="container-page py-16">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-64 h-8 bg-gray-200 rounded animate-pulse mx-auto mb-4" />
              <div className="w-80 h-6 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
              <div className="w-72 h-6 bg-gray-200 rounded animate-pulse mx-auto mb-8" />
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
                <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-white" data-section="competitor">
        <div className="container-page container-top">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 text-red-500">⚠</div>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Comparisons</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const listedCompetitors = competitors.filter((c) => !c.unlisted);
  const featuredCompetitors = listedCompetitors.filter((c) => c.featured);
  const nonFeaturedCompetitors = listedCompetitors.filter((c) => !c.featured);

  return (
    <div className="min-h-screen bg-white" data-section="competitor">
      {/* 3rd Party Toggle - Using reusable component */}
      <ThirdPartyToggle className="fixed top-20 right-4 z-40" />

      {/* Nowadays VS Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="container-page py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_0.5fr_2fr] gap-12 items-center">
            {/* Left side - Nowadays */}
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-48 h-48 rounded-xl flex items-center justify-center overflow-hidden">
                <img src={nowadaysIcon.src} alt="Nowadays Icon" className="w-36 h-36 object-contain" />
              </div>
              <h2 className="text-h2 text-landing-primary text-center">Nowadays</h2>
              <p className="text-body-lg text-landing-secondary max-w-md text-center">
                {isThirdParty
                  ? "Discover why leading agencies choose Nowadays to deliver exceptional events for their clients."
                  : "See how Nowadays compares to other event planning platforms and find the best solution for your needs."}
              </p>
            </div>

            {/* VS Text */}
            <div className="flex items-center justify-center">
              <div className="text-h2 text-landing-primary">vs</div>
            </div>

            {/* Right side - Competitor Grid */}
            <div className="grid grid-cols-3 gap-4">
              {listedCompetitors.slice(0, 9).map((competitor) => (
                <div
                  key={competitor.id}
                  onClick={() => handleCompetitorClick(competitor)}
                  className="aspect-square bg-gray-50 rounded-lg flex items-center justify-center p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <img
                    src={competitor.competitorLogo.url}
                    alt={competitor.competitorLogo.alt}
                    className="w-full h-full object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparisons Grid */}
      <div className="container-page py-16">
        {/* All Comparisons */}
        {(featuredCompetitors.length > 0 ? nonFeaturedCompetitors.length > 0 : listedCompetitors.length > 0) && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-8">
              {featuredCompetitors.length > 0 ? "More Comparisons" : "Platform Comparisons"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(featuredCompetitors.length > 0 ? nonFeaturedCompetitors : listedCompetitors).map((competitor) => (
                <VSCard key={competitor.id} competitor={competitor} onClick={() => handleCompetitorClick(competitor)} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div>
        <div className="container-page py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Try Nowadays?</h2>
            <p className="text-gray-600 mb-8">
              Experience AI-powered event planning that saves time and reduces costs. Start planning your next event
              today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/create">
                <Button variant="primary" color="primary" size="md">
                  Try our platform
                </Button>
              </Link>
              <Link href="/create">
                <Button variant="outline" color="neutral" size="md">
                  Create an Event
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <UnifiedModal isOpen={isModalOpen("cal")} onClose={() => closeModal("cal")} type="cal" />
    </div>
  );
}

// Main Competitor Page Component with Suspense
export default function Competitor() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-white" data-section="competitor">
          <div className="flex items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        </div>
      }
    >
      <CompetitorInner />
    </Suspense>
  );
}
